import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation, Outlet, Navigate } from "react-router-dom";
import { complianceNavChildren } from './complianceNavChildren';
import { apiLog } from "../utils/debugConfig.js";
import { useAuth } from "../AuthContext.jsx";
import { useOrganizationTheme } from "../hooks/useOrganizationTheme"; // Multi-tenant theme support
import { useOrganizationNavigation } from "../hooks/useOrganizationNavigation"; // Organization-aware navigation
import { useTheme } from "../hooks/useTheme.jsx"; // Theme detection for dark mode
import { useVersionedApi } from "../utils/versionedApi";
import { useOrgFeatures } from '../hooks/useOrgFeatures';
import {
  ORG_BUNDLE_SLUGS_STORAGE_KEY,
  ORG_BUNDLE_SLUGS_UPDATED_EVENT,
} from '../hooks/useOrgBundleSlugs';
import { useAccessControl } from "../hooks/useAccessControl.jsx"; // Flexible RBAC access control
import { useFeatureVisibility } from '../contexts/FeatureVisibilityContext';
import { useIntegratedSidebar } from "../hooks/useIntegratedSidebar"; // Integrated permissions sidebar
import CommandPalette from '../components/ui/CommandPalette';
import KeyboardShortcutsHelp from '../components/ui/KeyboardShortcutsHelp';
import LiveSupport from '../components/support/LiveSupport';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import PageTransition from '../components/ui/PageTransition';
import ErrorBoundary from '../components/ErrorBoundary';
import { useHotkeys } from '../hooks/useHotkeys';
// WeCare logo removed - using AraSync as default fallback for all organizations
import araSyncMarkSvg from '../assets/AraSync-Solid-Core-Mark.svg';
import ChangePasswordModal from './ChangePasswordModal.jsx';
import AlertsDropdown from '../components/AlertsDropdown.jsx';
import NotificationCenter from '../components/ui/NotificationCenter';
import { ThemeToggleButton } from '../components/ThemeToggle.jsx';
import './AdminShell.css';
import { safeStorage } from '../utils/safeStorage';
import { keepInsideViewport } from './utils/floatingButtonPosition';
import { toast } from 'react-toastify';
import OnCallPopup from '../components/OnCallPopup';
import { getAdminShellViewportMode, sidebarStartsOpenForWidth } from './adminShellResponsive';
import useMobileNavigationDrawer from './useMobileNavigationDrawer';
import { isCaseManagementProgramName, isHousingSupportProgramName, isResidentialProgramName } from '../utils/programTypeAccess.js';
import { isFieldStaffWebUser, fieldStaffMayOpen, fieldStaffHomeRedirect, trimNavigationForFieldStaff } from '../utils/fieldStaffWebAccess';
import FieldStaffWebHome from './FieldStaffWebHome';
import {
  canAccessCamhiGrantBilling,
  canAccessOwnerBillingAgent,
  canAccessOwnerKnowledgeBase,
  getOrganizationAdminAccess,
} from './utils/organizationAdminAccess';
import {
  UserIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronUpIcon,
  HomeIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  AcademicCapIcon,
  UserGroupIcon,
  HeartIcon,
  BeakerIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  MegaphoneIcon,
  PhoneIcon,
  PrinterIcon,
  CogIcon,
  ChevronRightIcon,
  ClockIcon,
  CalendarIcon,
  FolderIcon,
  QueueListIcon,
  InboxArrowDownIcon,
  DocumentMagnifyingGlassIcon,
  CalculatorIcon,
  IdentificationIcon,
  ShieldExclamationIcon,
  AdjustmentsHorizontalIcon,
  DocumentDuplicateIcon,
  CurrencyDollarIcon,
  ChartBarSquareIcon,
  BellAlertIcon,
  HomeModernIcon,
  ArrowUpTrayIcon,
  BoltIcon,
  EyeIcon,
  ChevronLeftIcon,
  CalendarDaysIcon,
  FunnelIcon,
  LifebuoyIcon,
  Bars3Icon, XMarkIcon
, BookOpenIcon, DevicePhoneMobileIcon, ServerStackIcon, PlayCircleIcon, TableCellsIcon } from '@heroicons/react/24/outline';

import { useNavigationFrictionTracking } from '../config/navigationFriction';
import { peopleHrNav } from './peopleHr/peopleHrNav';

export default function AdminShell() {
  const { navigate } = useOrganizationNavigation(); // Organization-aware navigation
  const location = useLocation();

  // Reports A→B→A route round trips to PostHog as a wayfinding signal. Mounted
  // here because the admin shell wraps every admin route, so one mount covers
  // the whole surface. See config/navigationFriction.js.
  useNavigationFrictionTracking();
  const { user, loading, logout, can, canSee } = useAuth();
  const { canManageRoles, canManageFeatureVisibility } = getOrganizationAdminAccess(can);
  const { isVisible } = useFeatureVisibility();
  const {
    hasFeature: hasOrgFeature,
    hasExternalIntegration,
  } = useOrgFeatures();
  const { getColor, theme: organization } = useOrganizationTheme(); // Multi-tenant theme support
  const { isDark } = useTheme(); // Theme detection for dark mode
  const { filterResources, loading: accessLoading } = useAccessControl(); // Access control
  const {
    canAccessProgram,
    hasPermission,
    loading: sidebarLoading,
    // Programs now flow through the shared IntegratedSidebar Context — no need
    // for AdminShell to fetch /programs separately (it used to). One fetch,
    // one source of truth, no flip-flop between sidebar and dashboard body.
    programs: sharedPrograms,
  } = useIntegratedSidebar();
  const profileDropdownRef = useRef(null);
  const profilePanelRef = useRef(null);
  const mainContentRef = useRef(null);
  const [isActuallyDark, setIsActuallyDark] = useState(false);

  // Check dark mode from both hook and DOM
  useEffect(() => {
    const checkDarkMode = () => {
      const htmlHasDark = document.documentElement.classList.contains('dark');
      const actuallyDark = isDark || htmlHasDark;
      setIsActuallyDark(actuallyDark);
    };

    checkDarkMode();

    // Also observe DOM changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, [isDark]);

  const apiClient = useVersionedApi('v1');

  // Draggable Policy Assistant FAB state
  const fabRef = useRef(null);
  const fabDragState = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0, hasMoved: false });
  const [fabPosition, setFabPosition] = useState(() => {
    try {
      const saved = safeStorage.getItem('policyAssistantFabPos');
      if (saved) return JSON.parse(saved);
    } catch {}
    return null; // null means use default bottom-right
  });
  // Where the button is drawn: the saved spot, pulled inside the current
  // window. Worked out from the saved spot on every resize, so it goes back
  // where it was dragged when the window grows again.
  const [viewportSize, setViewportSize] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
  useEffect(() => {
    const onResize = () => setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const fabDisplayPosition = keepInsideViewport(fabPosition, viewportSize);

  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  useHotkeys({
    'mod+k': () => { setShowShortcuts(false); setShowNotifications(false); setShowCommandPalette(true); },
    'mod+/': () => { setShowCommandPalette(false); setShowNotifications(false); setShowShortcuts(prev => !prev); },
    'mod+shift+n': () => { setShowCommandPalette(false); setShowShortcuts(false); setShowNotifications(prev => !prev); },
    'mod+s': { handler: () => { /* prevent browser save dialog — forms handle saving via their own submit */ }, preventDefault: true },
  });

  const handleFabPointerDown = useCallback((e) => {
    const fab = fabRef.current;
    if (!fab) return;
    const rect = fab.getBoundingClientRect();
    fabDragState.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      startLeft: rect.left,
      startTop: rect.top,
      hasMoved: false
    };
    fab.setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const handleFabPointerMove = useCallback((e) => {
    const ds = fabDragState.current;
    if (!ds.isDragging) return;
    const dx = e.clientX - ds.startX;
    const dy = e.clientY - ds.startY;
    if (!ds.hasMoved && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
    ds.hasMoved = true;
    const newLeft = Math.max(0, Math.min(window.innerWidth - 56, ds.startLeft + dx));
    const newTop = Math.max(0, Math.min(window.innerHeight - 56, ds.startTop + dy));
    setFabPosition({ left: newLeft, top: newTop });
  }, []);

  const handleFabPointerUp = useCallback((e) => {
    const ds = fabDragState.current;
    if (!ds.isDragging) return;
    ds.isDragging = false;
    if (ds.hasMoved) {
      const fab = fabRef.current;
      if (fab) {
        const rect = fab.getBoundingClientRect();
        const pos = { left: rect.left, top: rect.top };
        setFabPosition(pos);
        try { safeStorage.setItem('policyAssistantFabPos', JSON.stringify(pos)); } catch {}
      }
    } else {
      // It was a click, not a drag — open the popup
      const width = 900;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      window.open(
        '/policy-assistant',
        'PolicyAssistant',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
    }
  }, []);

  // Organization change tracking (debug logs removed)
  useEffect(() => {
    // Organization and user data tracking for state management
    // Debug logs removed to reduce console noise
  }, [organization, user]);

  // State for sidebar navigation
  const initialViewportWidth = typeof window === 'undefined' ? 1200 : window.innerWidth;
  const [viewportMode, setViewportMode] = useState(() => getAdminShellViewportMode(initialViewportWidth));
  const viewportModeRef = useRef(getAdminShellViewportMode(initialViewportWidth));
  const [sidebarOpen, setSidebarOpen] = useState(() => sidebarStartsOpenForWidth(initialViewportWidth));
  const isMobile = viewportMode === 'mobile';
  const mobileDrawerOpen = isMobile && sidebarOpen;
  const sidebarRef = useRef(null);
  const navigationTriggerRef = useRef(null);
  const closeMobileNavigation = useCallback(() => setSidebarOpen(false), []);
  useMobileNavigationDrawer({ open: mobileDrawerOpen, drawerRef: sidebarRef, triggerRef: navigationTriggerRef, onClose: closeMobileNavigation });
  // Programs come from the IntegratedSidebar context — no local fetch.
  const programs = sharedPrograms;
  const LOADING_PROGRAMS = sidebarLoading;
  const [orgBundles, setOrgBundles] = useState([]);
  const [bundlesLoaded, setBundlesLoaded] = useState(false);
  const [bundlesLoadFailed, setBundlesLoadFailed] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    admissions: false,
    clinical: false,
    programs: false,
    billing: false,
    communication: false,
    reports: false,
    compliance: false,
    peopleHr: false,
    admin: false,
    // Legacy sections (kept for backward compatibility)
    patients: false,
    visits: false,
    caregivers: false,
    contacts: false,
    analytics: false
  });

  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // Keep the sidebar stable through Safari's frequent height-only resize events.
  // Only reset it when the viewport actually crosses a phone/tablet/desktop
  // width boundary. Tablet portrait starts with the compact 72px navigation rail.
  useEffect(() => {
    const checkScreenSize = () => {
      const nextMode = getAdminShellViewportMode(window.innerWidth);
      setViewportMode(nextMode);

      if (viewportModeRef.current !== nextMode) {
        setSidebarOpen(nextMode === 'desktop');
        viewportModeRef.current = nextMode;
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Close sidebar on mobile when navigating
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
    // AdminShell's main element, rather than window, owns page scrolling. Reset
    // both axes so Safari cannot restore a stale horizontal offset after login
    // or history navigation and slide the page underneath the sidebar.
    mainContentRef.current?.scrollTo?.({ top: 0, left: 0 });
    if (mainContentRef.current) {
      mainContentRef.current.scrollLeft = 0;
    }
  }, [location.pathname]);

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const isClosing = prev[section];
      // Collapse all sections, then toggle the clicked one
      const allClosed = Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {});
      return {
        ...allClosed,
        [section]: !isClosing
      };
    });
  };

  const showRBAC = !loading && user && user.role === "super_admin";

  // Check if we're in platform admin mode
  const isPlatformMode = location.pathname.startsWith('/platform');

  // Check if organization has a specific bundle (complete-care is superset of all)
  const orgHasBundle = (slug) => {
    // Entitlements are security boundaries, not a UI refinement. Keep bundle-gated
    // navigation hidden until entitlement discovery succeeds, and never fail open
    // when the response is empty or unavailable.
    if (!bundlesLoaded || bundlesLoadFailed || !orgBundles) return false;
    return orgBundles.some(ob => ob.bundle?.slug === slug || ob.bundle?.slug === 'complete-care');
  };

  // Detect if org has ONLY the mh-co-responder bundle (flatten UI)
  const isCoResponderOnly = useMemo(() => {
    if (!orgBundles || orgBundles.length === 0) return false;
    const slugs = orgBundles.map(ob => ob.bundle?.slug).filter(Boolean);
    return slugs.length === 1 && slugs[0] === 'mh-co-responder';
  }, [orgBundles]);

  // Org holds the co-responder bundle (may also hold others). Matches the
  // backend requireBundle('mh-co-responder') gate and the in-profile panel gate.
  const hasCoResponderBundle = useMemo(() => {
    if (!orgBundles || orgBundles.length === 0) return false;
    return orgBundles.some(ob => ob.bundle?.slug === 'mh-co-responder');
  }, [orgBundles]);

  const coResponderProgramId = useMemo(() => {
    if (!isCoResponderOnly) return null;
    const match = programs.find(p => {
      const n = p.name.toLowerCase();
      return n.includes('co-responder') || n.includes('coresponder');
    });
    return match?.id || null;
  }, [isCoResponderOnly, programs]);

  // Enhanced navigation structure with icons and grouping
  const getSidebarNavigation = () => {
    const navigation = [];

    // Platform Admin Navigation. Grouped under non-clickable headings
    // (item.isHeading) so the console reads Operate / Insight / Platform.
    if (isPlatformMode) {
      const platformItem = (id, name, href, icon) => ({
        id, name, href, icon, isActive: location.pathname === href, current: location.pathname === href
      });
      const platformHeading = (id, name) => ({ id, name, isHeading: true });

      navigation.push(platformHeading('platform-group-operate', 'Operate'));
      navigation.push(platformItem('platform-dashboard', 'Dashboard', '/platform', HomeIcon));
      navigation.push(platformItem('organizations', 'Organizations', '/platform/organizations', BuildingOfficeIcon));
      navigation.push(platformItem('platform-billing', 'Customer Billing', '/platform/billing', BanknotesIcon));
      navigation.push(platformItem('platform-support', 'Support Inbox', '/platform/support', ChatBubbleLeftRightIcon));
      navigation.push(platformItem('platform-live-support', 'Live Support', '/platform/live-support', ChatBubbleLeftRightIcon));
      navigation.push(platformItem('platform-demo-scheduling', 'Demo Scheduling', '/platform/demo-scheduling', CalendarDaysIcon));
      navigation.push(platformItem('platform-crm', 'Sales CRM', '/platform/crm', ChartBarIcon));

      navigation.push(platformHeading('platform-group-insight', 'Insight'));
      navigation.push(platformItem('platform-usage-analytics', 'Usage Analytics', '/platform/usage-analytics', ChartBarIcon));
      navigation.push(platformItem('platform-friction-analysis', 'Friction Analytics', '/platform/friction-analysis', ExclamationTriangleIcon));
      navigation.push(platformItem('platform-ai-usage', 'AI Usage', '/platform/ai-usage', CpuChipIcon));
      navigation.push(platformItem('platform-system-health', 'System Health', '/platform/system-health', ServerStackIcon));

      navigation.push(platformHeading('platform-group-platform', 'Platform'));
      navigation.push(platformItem('platform-admins', 'Admins', '/platform/admins', ShieldCheckIcon));
      navigation.push(platformItem('platform-branding', 'Branding', '/platform/branding', CogIcon));
      navigation.push(platformItem('platform-app-updates', 'App Updates', '/platform/app-updates', DevicePhoneMobileIcon));

      return navigation;
    }

    // 🚨 CRITICAL: Wait for integrated sidebar AND programs to load before building navigation
    // This prevents race condition where navigation renders with empty permissions or programs
    // For non-super-admin users, also ensure permissions are populated
    const permissionsReady = user?.role === 'super_admin' ||
                             (user?.sidebarItems && user.sidebarItems.length > 0) ||
                             (user?.resolvedPermissions && user.resolvedPermissions.length > 0);

    // NOTE: intentionally NOT gated on bundlesLoaded. Bundles are a *refinement*
    // (orgHasBundle hides a few items once loaded; the server already filters
    // sidebarItems and requireBundle enforces on the API). Hard-gating the whole
    // sidebar on /bundles/my-bundles made a single slow/stalled request brick the
    // entire nav (recurring "blank sidebar" — prod during a Redis blip, and local
    // dev round-tripping cross-region to prod). The nav now renders as soon as
    // permissions/sidebar/programs are ready; bundle-gated items appear when
    // /bundles/my-bundles resolves.
    if (sidebarLoading || LOADING_PROGRAMS || !permissionsReady) {
      apiLog('⏳ Waiting for sidebar/programs/permissions/bundles to load...', {
        sidebarLoading,
        LOADING_PROGRAMS,
        permissionsReady,
        bundlesLoaded
      });
      return []; // Return empty navigation while loading
    }

    // Regular Admin Navigation - Workflow-based order
    apiLog('🔐 Building navigation with canSee() permissions');
    
    // 1. Dashboard (always visible - starting point)
    const showDashboard = (canSee('dashboard') || true) && isVisible('sidebar.dashboard'); // Dashboard always visible as fallback
    if (showDashboard) {
      navigation.push({
        id: 'dashboard',
        name: 'Dashboard',
        href: '/admin',
        icon: HomeIcon,
        current: location.pathname === '/admin'
      });
    }

    if (organization?.slug === 'wecare-health' && user?.email?.trim().toLowerCase() === 'ola@wecaremn.org') {
      navigation.push({ id: 'ceo', name: 'CEO workspace', href: '/admin/ceo', icon: ChartBarIcon, current: location.pathname === '/admin/ceo' });
    }

    // Co-Responder-only: Add Caseload and Documents as top-level sidebar items
    if (isCoResponderOnly) {
      navigation.push({
        id: 'caseload',
        name: 'Caseload',
        href: '/admin/caseload',
        icon: UserGroupIcon,
        current: location.pathname === '/admin/caseload'
      });
      navigation.push({
        id: 'co-responder-documents',
        name: 'Documents',
        href: '/admin/co-responder-documents',
        icon: DocumentTextIcon,
        current: location.pathname === '/admin/co-responder-documents'
      });
    }

    // Community provider catalog: available to any org holding the co-responder
    // bundle (backend routes are gated by requireBundle('mh-co-responder')),
    // not just co-responder-only orgs.
    if (hasCoResponderBundle) {
      navigation.push({
        id: 'community-providers',
        name: 'Community Directory',
        href: '/admin/community-providers',
        icon: UserGroupIcon,
        current: location.pathname === '/admin/community-providers'
      });
    }

    // Encampments: co-responder bundle only; backend is gated by
    // requireBundle('mh-co-responder') and the view_encampments permission.
    if (hasCoResponderBundle && canSee('encampments')) {
      navigation.push({
        id: 'encampments',
        name: 'Encampments',
        href: '/admin/encampments',
        icon: MapPinIcon,
        current: location.pathname === '/admin/encampments'
      });
    }

    // 2. Patients Section (standalone)
    const showPatients = canSee('patients') && isVisible('sidebar.clients');

    if (showPatients) {
      navigation.push({
        id: 'patients',
        name: 'Clients',
        href: '/admin/patients',
        icon: UserGroupIcon
      });
    }

    // 2.5. Admissions Section (Referrals & Waitlist)
    const admissionsIntegrationEnabled = hasExternalIntegration('websiteIntegration');
    const showReferrals = canSee('referrals');
    const showWaitlist = canSee('waitlist');
    // DA Scheduling can be granted on its own (e.g. Therapists) WITHOUT unlocking
    // the Referrals/Intake Pipeline/Waitlist pages.
    const showDaScheduling = canSee('da-scheduling');

    if (admissionsIntegrationEnabled && (showReferrals || showWaitlist || showDaScheduling) && !isCoResponderOnly && isVisible('sidebar.admissions')) {
      const admissionsChildren = [];

      if (showReferrals) {
        admissionsChildren.push({ name: 'Referrals', href: '/admin/referrals', icon: InboxArrowDownIcon });
        admissionsChildren.push({ name: 'Intake Pipeline', href: '/admin/intake-pipeline', icon: FunnelIcon });
      }

      // Same gate as the route and the /da-slots API: a DA-scheduling permission
      // (or manage_referrals) and the Behavioral Health bundle. view_referrals
      // alone never passed the API, so it no longer shows the link.
      if ((showDaScheduling || hasPermission('manage_referrals')) && orgHasBundle('behavioral-health')) {
        admissionsChildren.push({ name: 'DA Scheduling', href: '/admin/da-scheduling', icon: CalendarDaysIcon });
      }

      if (showWaitlist) {
        admissionsChildren.push({ name: 'Waitlist', href: '/admin/waitlist', icon: QueueListIcon });
      }

      navigation.push({
        id: 'admissions',
        name: 'Admissions',
        icon: InboxArrowDownIcon,
        expandable: true,
        expanded: expandedSections.admissions,
        children: admissionsChildren
      });
    }

    // 2.6. Providers Section (staff management - moved up for workflow)
    // Uses integrated sidebar permissions (MHP, LPN, PCA, etc. are excluded by default)
    const showProviders = canSee('providers') && isVisible('sidebar.providers');

    if (showProviders) {
      navigation.push({
        id: 'caregivers',
        name: 'Providers',
        href: '/admin/caregivers',
        icon: IdentificationIcon // Clients already uses the group icon
      });
    }

    // 3. Clinical Section (standalone) - requires behavioral-health bundle
    const showClinical = orgHasBundle('behavioral-health') && canSee('clinical') && isVisible('sidebar.clinical');

    if (showClinical) {
      navigation.push({
        id: 'clinical',
        name: 'Clinical',
        href: '/admin/clinical',
        icon: HeartIcon
      });
    }

    // 3.5. Appointments Section (standalone) - requires behavioral-health bundle
    if (orgHasBundle('behavioral-health') && canSee('appointments') && isVisible('sidebar.appointments')) {
      navigation.push({
        id: 'appointments',
        name: 'Appointments',
        href: '/admin/appointments',
        icon: CalendarIcon
      });
    }

    // 3.6. Clinical Chart (standalone) - requires behavioral-health bundle
    if (orgHasBundle('behavioral-health') && canSee('clinicalChart') && isVisible('sidebar.clinical-chart')) {
      navigation.push({
        id: 'clinical-chart-report',
        name: 'Clinical Chart',
        href: '/admin/clinical-chart-report',
        icon: FolderIcon
      });
    }

    // 3.7. Client Stat Surveys (standalone) - satisfaction surveys
    if (can && can('client_stat_surveys_manage') && !isCoResponderOnly) {
      navigation.push({
        id: 'client-stat-surveys',
        name: 'Client Stat Surveys',
        href: '/admin/client-stat-surveys',
        icon: ClipboardDocumentCheckIcon,
        current: location.pathname === '/admin/client-stat-surveys'
      });
    }

    // 4. Programs Section - Dynamic programs from database
    const showPrograms = canSee('programs') && isVisible('sidebar.programs');

    if (showPrograms && programs.length > 0 && !isCoResponderOnly) {
      // Use dynamic access control filtering instead of hardcoded checks
      let filteredPrograms = filterResources('programs', programs);

      // Super admin bypass - always show all programs
      if (user?.role === 'super_admin') {
        apiLog(`👑 Super admin: showing all ${filteredPrograms.length} programs (bypassing all filtering)`);
      } else if (user?.role === 'admin') {
        apiLog(`🔐 Admin: showing all ${filteredPrograms.length} programs`);
      } else if (!sidebarLoading) {
        // Filter by program-specific access permissions (access_behavioral_health, access_adult_day, etc.)
        // manage_programs controls create/edit ability, NOT program-type visibility
        const beforeFilter = filteredPrograms.length;
        filteredPrograms = filteredPrograms.filter(program => canAccessProgram(program.name));
        apiLog(`🔐 Filtered programs from ${beforeFilter} to ${filteredPrograms.length} based on specific access`);
      }

      apiLog(`🔐 Programs filtering: ${programs.length} total -> ${filteredPrograms.length} filtered`);

      // Create dynamic program children from filtered programs
      const programChildren = filteredPrograms.map(program => {
        // Use program ID for stable, multi-tenant URLs
        // IDs don't change even if program names are updated across organizations

        // Determine appropriate icon based on program name/type
        let icon = AcademicCapIcon; // default
        const nameCheck = program.name.toLowerCase();
        if (nameCheck.includes('245d')) {
          icon = HomeModernIcon;
        } else if (isCaseManagementProgramName(nameCheck)) {
          // Before 'mental' / 'health' below: MH-TCM programs are named for mental health.
          icon = ClipboardDocumentListIcon;
        } else if (isResidentialProgramName(nameCheck)) {
          // 144G Residential Services; checked before 'care' below, which
          // would otherwise catch "assisted living ... care" names.
          icon = HomeIcon;
        } else if (isHousingSupportProgramName(nameCheck)) {
          // Minnesota Housing Support (chapter 256I)
          icon = HomeIcon;
        } else if (nameCheck.includes('co-responder') || nameCheck.includes('coresponder')) {
          icon = ShieldCheckIcon;
        } else if (nameCheck.includes('personal') || nameCheck.includes('care') || nameCheck.includes('pca')) {
          icon = UserGroupIcon;
        } else if (nameCheck.includes('behavioral') || nameCheck.includes('mental') || nameCheck.includes('health')) {
          icon = HeartIcon;
        } else if (nameCheck.includes('therapy') || nameCheck.includes('outpatient')) {
          icon = BeakerIcon;
        } else if (nameCheck.includes('day') || nameCheck.includes('adult')) {
          icon = BuildingOfficeIcon;
        }

        return {
          name: program.displayName || program.name,
          href: `/admin/programs/${program.id}`,
          icon: icon,
          programId: program.id,
          badge: program.isActive ? 'Active' : 'Inactive'
        };
      });

      // Only show Programs section if there are programs to display
      if (programChildren.length > 0) {
        navigation.push({
          id: 'programs',
          name: 'Programs',
          icon: AcademicCapIcon,
          expandable: true,
          expanded: expandedSections.programs,
          children: programChildren
        });
      }
    }

    // 5. (Providers moved up to section 2.6)

    // 6. Visit Management Section - MOVED to WeCare Home Health Program tabs
    // These features are now available under /admin/home-health-program as tabs
    // Keeping routes functional for backward compatibility but removing from sidebar

    // 6.5 Service Lines (top-level sidebar item)
    const showServiceLines = canSee('serviceLines') && isVisible('sidebar.service-lines');
    if (showServiceLines && orgHasBundle('billing')) {
      navigation.push({
        id: 'serviceLines',
        name: 'Service Lines',
        icon: QueueListIcon, // Time Tracking already uses the clock
        href: '/admin/service-lines',
        current: location.pathname.startsWith('/admin/service-lines')
      });
    }

    // 7. Eligibility (standalone section)
    const hasEligibilityIntegration = ['mnits', 'availity']
      .some((provider) => hasExternalIntegration(provider));
    const showEligibility = hasEligibilityIntegration &&
      canSee('eligibility') && isVisible('sidebar.eligibility');

    if (showEligibility && orgHasBundle('billing')) {
      navigation.push({
        id: 'eligibility',
        name: 'Eligibility',
        icon: DocumentCheckIcon,
        href: '/admin/eligibility',
        current: location.pathname.startsWith('/admin/eligibility')
      });
    }

    // 8. Billing Section (consolidated financial operations)
    const showBilling = canSee('billing') && isVisible('sidebar.billing');

    if (showBilling && orgHasBundle('billing')) {
      const billingChildren = [];

      // Core billing items
      billingChildren.push(
        { name: 'Billing Dashboard', href: '/admin/billing', icon: ChartBarIcon },
        { name: 'Invoice Generator', href: '/admin/billing/invoice-generator', icon: DocumentTextIcon },
        { name: 'Billing Batches', href: '/admin/billing/batches', icon: ClipboardDocumentListIcon },
        { name: 'Claim Status', href: '/admin/billing/claim-status', icon: ClipboardDocumentListIcon },
        { name: 'Denial Management', href: '/admin/billing/denials', icon: ShieldExclamationIcon },
        { name: 'Service Codes', href: '/admin/billing/service-codes', icon: ClipboardDocumentListIcon },
        { name: 'Payer Management', href: '/admin/billing/payers', icon: BuildingOfficeIcon },
        { name: 'ERA Reconciliation', href: '/admin/billing/era-reconciliation', icon: InboxArrowDownIcon },
        { name: 'Client Statement', href: '/admin/billing/client-statement', icon: UserIcon }
      );

      if (canAccessCamhiGrantBilling(hasOrgFeature)) {
        billingChildren.push(
          { name: 'Grant Invoices', href: '/admin/billing/grant-invoices', icon: CurrencyDollarIcon },
          { name: 'Grant Report', href: '/admin/billing/grant-report', icon: ChartBarIcon }
        );
      }

      // Owner-only billing automation. The backend independently enforces the
      // same organization entitlement for direct API requests.
      if (canAccessOwnerBillingAgent(hasOrgFeature)) {
        billingChildren.push({ name: 'Billing Agent', href: '/admin/billing/billing-agent', icon: BoltIcon });
      }

      // Accounting Ledger (reconciliation at end of revenue cycle)
      const showAccounting = canSee('accounting');
      if (showAccounting) {
        billingChildren.push({ name: 'Accounting Ledger', href: '/admin/accounting', icon: CalculatorIcon });
      }

      navigation.push({
        id: 'billing',
        name: 'Billing',
        icon: BanknotesIcon,
        expandable: true,
        expanded: expandedSections.billing,
        children: billingChildren
      });
    }

    // 10. Analytics Section (insights & metrics)
    // Use integrated permissions as the primary source of truth
    const showAnalytics = canSee('analytics') && isVisible('sidebar.analytics');

    if (showAnalytics) {
      navigation.push({
        id: 'analytics',
        name: 'Analytics',
        icon: ChartBarIcon,
        href: '/admin/analytics',
        current: location.pathname === '/admin/analytics'
      });
    }

    // 11. Communication Section (messaging + contacts)
    const showCommunication = canSee('communication') && isVisible('sidebar.communication');

    const showContacts = canSee('contacts');

    // Text alerts is every user's OWN consent, so it is not permission-gated —
    // and it keeps the Communication group visible for staff who have neither
    // messaging nor contacts, who would otherwise have no route to it.
    const showTextAlerts = hasExternalIntegration('textAlerts');

    if ((showCommunication || showContacts || showTextAlerts) && !isCoResponderOnly) {
      const commChildren = [];

    if (showCommunication) {
      commChildren.push({ name: 'Messaging', href: '/admin/communication', icon: ChatBubbleLeftRightIcon });
    }

      if (showContacts) {
        commChildren.push({ name: 'Contacts', href: '/admin/contacts', icon: UserGroupIcon });
      }

      if (showTextAlerts) {
        commChildren.push({ name: 'Text Message Alerts', href: '/admin/text-alerts', icon: ChatBubbleLeftRightIcon });
      }

      const canUseFaxCenter = ['admin', 'super_admin'].includes(user?.role)
        || hasPermission('fax_view')
        || hasPermission('fax_send');
      if (hasExternalIntegration('fax') && canUseFaxCenter) {
        commChildren.push({ name: 'Fax Center', href: '/admin/fax', icon: PrinterIcon });
      }

      // Announcements + On-Call supervisor - admin only
      if (user?.role === 'admin' || user?.role === 'super_admin') {
        commChildren.push({ name: 'Announcements', href: '/admin/announcements', icon: MegaphoneIcon });
        if (hasExternalIntegration('textAlerts')) {
          commChildren.push({ name: 'SMS Opt-Ins', href: '/admin/sms-consent', icon: ChatBubbleLeftRightIcon });
        }
        commChildren.push({ name: 'On-Call Rotation', href: '/admin/on-call', icon: PhoneIcon });
      }

      navigation.push({
        id: 'communication',
        name: 'Communication',
        icon: ChatBubbleLeftRightIcon,
        expandable: true,
        expanded: expandedSections.communication,
        children: commChildren
      });
    }

    // 12.4. Resources — BH staff quick reference (county crisis numbers, etc.).
    // Gated on the view_resources permission (sidebarItem 'resources'), which is
    // granted only to BH-program roles — see integratedPermissions.js.
    if (canSee('resources')) {
      navigation.push({
        id: 'resources',
        name: 'Resources',
        icon: LifebuoyIcon,
        href: '/admin/resources',
        current: location.pathname === '/admin/resources'
      });
    }

    // 12.45. Training Library: how-to videos (view_training_library, sidebar
    // item 'training'). Field staff keep it: they watch the videos made for them.
    if (canSee('training')) {
      navigation.push({
        id: 'training',
        name: 'Training Videos',
        icon: PlayCircleIcon,
        href: '/admin/training',
        current: location.pathname === '/admin/training' || location.pathname.startsWith('/admin/training/')
      });
    }

    // 12.5. Time Tracking Section. Two modes: non-billable clinical time (BH) and
    // driver/ADS-supervisor clock in-out via driverShift. Bundle scoping lives in
    // bundleCapabilities.js ('timeTracking' -> behavioral-health, 245D or adult-day-services)
    // and is applied server-side to sidebarItems; a hardcoded behavioral-health
    // check here used to lock ADS drivers out of their own timeclock.
    const showTimeTracking = canSee('timeTracking') && isVisible('sidebar.time-tracking');

    if (showTimeTracking) {
      navigation.push({
        id: 'time-tracking',
        name: 'Time Tracking',
        icon: ClockIcon,
        href: '/admin/time-tracking',
        current: location.pathname === '/admin/time-tracking'
      });
    }

    // 12.7. Caregiver Requests — MOVED to WeCare Home Health Program tabs
    // (pages.home-health-program.tabs.caregiver-requests). The /admin/caregiver-requests
    // route stays functional for backward compatibility but is no longer in the sidebar.

    // 13. Reports Section (documentation)
    // Use integrated permissions as the primary source of truth
    const showReports = canSee('reports') && isVisible('sidebar.reports');

    if (showReports && !isCoResponderOnly) {
      navigation.push({
        id: 'reports',
        name: 'Reports',
        icon: DocumentTextIcon,
        expandable: true,
        expanded: expandedSections.reports,
        children: [
          { name: 'General Reports', href: '/admin/reports', icon: DocumentTextIcon }
        ]
      });
    }

    // 14. Compliance Section (regulatory) — per-item permission gating
    const showCompliance = canSee('compliance') && isVisible('sidebar.compliance');

    if (showCompliance) {
      const complianceChildren = [];

      // Each link needs its own permission (the same one its page route and API
      // check); bundle rules are applied server-side to the canSee() keys.
      // PCA Supervision also needs Home Health program access,
      // the same check the API makes (requireProgramAccess('home-health')).
      const hasHomeHealthAccess = ['admin', 'super_admin'].includes(user?.role) || hasPermission('access_home_health');
      // The five areas the Compliance Overview scores come first, named as
      // its cards and the pages' titles are; the other compliance pages sit
      // under a "Tools" sub-heading.
      const complianceLinks = [
        { key: 'overview', permission: 'view_compliance_overview', name: 'Overview', href: '/admin/compliance/overview', icon: ChartBarSquareIcon },
        { key: 'staffCredentials', permission: 'view_staff_credentials', name: 'Staff Credentials', href: '/admin/compliance/staff-credentials', icon: IdentificationIcon },
        { key: 'authGuardrails', permission: 'view_auth_guardrails', name: 'Authorizations', href: '/admin/compliance/authorization-guardrails', icon: ShieldExclamationIcon },
        { key: 'documentExpiration', permission: 'view_document_expiration', name: 'Document Expiration', href: '/admin/compliance/document-expiration', icon: ClockIcon },
        { key: 'pcaSupervision', permission: 'manage_compliance', homeHealth: true, name: 'PCA Supervision', href: '/admin/compliance/pca-supervision', icon: ClipboardDocumentListIcon },
        { key: 'billingCompliance', permission: 'view_billing_compliance', name: 'Billing Compliance', href: '/admin/compliance/billing-compliance', icon: CurrencyDollarIcon },
        { key: 'auditLogs', tool: true, permission: 'view_audit_logs', name: 'Audit Logs', href: '/admin/compliance/audit-logs', icon: DocumentTextIcon },
        { key: 'ruleEngine', tool: true, permission: 'view_rule_engine', name: 'Rule Engine', href: '/admin/compliance/rule-engine', icon: AdjustmentsHorizontalIcon },
        { key: 'narrativeSimilarity', tool: true, permission: 'view_narrative_similarity', name: 'Narrative Similarity', href: '/admin/compliance/narrative-similarity', icon: DocumentDuplicateIcon },
        { key: 'recordsRequests', tool: true, permission: 'manage_records_requests', name: 'Records Requests', href: '/admin/compliance/records-requests', icon: DocumentMagnifyingGlassIcon },
        { key: 'dataReadiness', tool: true, permission: 'view_data_readiness', name: 'Data Readiness', href: '/admin/compliance/data-readiness', icon: BeakerIcon, badge: 'Coming Soon' },
      ];
      const visibleComplianceLinks = complianceLinks
        .filter(link => canSee(`compliance.${link.key}`) && hasPermission(link.permission) && (!link.homeHealth || hasHomeHealthAccess));
      complianceChildren.push(...complianceNavChildren(visibleComplianceLinks));
      if (complianceChildren.length > 0 && !isCoResponderOnly) {
        navigation.push({
          id: 'compliance',
          name: 'Compliance',
          icon: ShieldCheckIcon,
          expandable: true,
          expanded: expandedSections.compliance,
          children: complianceChildren
        });
      }
    }

    const hrChildren = peopleHrNav({ organizationSlug: organization?.slug, user, hasPermission, pathname: location.pathname, search: location.search });
    if (hrChildren) {
      navigation.push({ id: 'peopleHr', name: 'People & HR', icon: UserGroupIcon, expandable: true, expanded: expandedSections.peopleHr, children: hrChildren });
    }

    // 15. Administration Section (system settings)
    // Use integrated permissions for granular control
    const showAdministration = (canSee('administration') || user?.role === 'super_admin') && isVisible('sidebar.administration');
    
    if (showAdministration) {
      const adminChildren = [];
      
      // Program Management
      if (canSee('administration.programs')) {
        adminChildren.push({ name: 'Program Management', href: '/admin/programs', icon: BuildingOfficeIcon });
      }
      
      // Office Locations - healthcare-agency feature, hidden for co-responder-only orgs
      if (canSee('administration.offices') && !isCoResponderOnly) {
        adminChildren.push({ name: 'Office Locations', href: '/admin/offices', icon: MapPinIcon });
      }

      // Tenant-scoped organization settings. Platform-only controls inside the
      // page remain separately restricted to super_admin.
      if (can('manage_organization')) {
        adminChildren.push({ name: 'Organization Settings', href: '/admin/settings', icon: CogIcon });
      }

      // System Users
      if (canSee('administration.users')) {
        adminChildren.push({ name: 'System Users', href: '/admin/users', icon: UserGroupIcon });
      }

      // Provider Types - healthcare credential taxonomy, hidden for co-responder-only orgs
      if (canSee('administration.providerTypes') && !isCoResponderOnly) {
        adminChildren.push({ name: 'Provider Types', href: '/admin/provider-types', icon: UserGroupIcon });
      }

      // Tenant role management is controlled by an explicit permission. The
      // backend separately protects the platform super_admin role.
      if (canManageRoles) {
        adminChildren.push({ name: 'Permissions', href: '/admin/permissions', icon: ShieldCheckIcon });
      }

      // Notification Settings
      if (canSee('administration.notifications')) {
        adminChildren.push({ name: 'Notification Settings', href: '/admin/notification-settings', icon: BellAlertIcon });
      }

      // Tab-level checks distinguish BH sessions/workloads from Home Health visits.
      if ((canSee('administration.schedulingIntelligence') || user?.role === 'super_admin') && !isCoResponderOnly
        && (orgHasBundle('behavioral-health') || orgHasBundle('home-health'))
        && (hasPermission('access_behavioral_health') || hasPermission('access_home_health'))
        && hasExternalIntegration('azureAi')) { // every /ai/analytics endpoint requires it
        adminChildren.push({ name: 'Scheduling Intelligence', href: '/admin/ai-analytics', icon: ChartBarIcon });
      }

      // Clinical Imports - agency migration tooling, hidden for co-responder-only orgs
      if (canSee('administration.clinicalImports') && !isCoResponderOnly) {
        adminChildren.push({ name: 'Clinical Imports', href: '/admin/clinical-imports', icon: ArrowUpTrayIcon });
      }

      // Data import - clients, staff, service agreements and client PDFs from a
      // previous system (any of the four import_* permissions; any bundle).
      if (canSee('administration.dataImport') || user?.role === 'super_admin') {
        adminChildren.push({ name: 'Data Import', href: '/admin/data-import', icon: TableCellsIcon });
      }

      // Knowledge Base
      if (canAccessOwnerKnowledgeBase(canSee, hasOrgFeature)) {
        adminChildren.push({ name: 'Knowledge Base', href: '/admin/knowledge-base', icon: AcademicCapIcon });
      }

      // Infrastructure diagnostics belong only to the owner organization.
      // The page and /health API check view_system; the sidebar key comes from system_settings.
      if (canSee('administration.systemHealth') && hasPermission('view_system') && hasOrgFeature('system_health')) {
        adminChildren.push({ name: 'System Health', href: '/admin/system', icon: BeakerIcon });
      }

      // Usage Analytics - product-usage analytics sourced from PostHog.
      // The route requires super_admin + superAdminMiddleware regardless of
      // the ordinary Analytics permission group, so the link must not be
      // shown to a tenant admin who would be refused with a 403.

      if (canManageFeatureVisibility) {
        adminChildren.push({ name: 'Feature Visibility', href: '/admin/feature-visibility', icon: EyeIcon });
      }

      // Only show Administration section if there are items to display
      if (adminChildren.length > 0) {
        navigation.push({
          id: 'admin',
          name: 'Administration',
          icon: CogIcon,
          expandable: true,
          expanded: expandedSections.admin,
          children: adminChildren
        });
      }
    }

    return navigation;
  };

  // Field staff (PCAs) see their own time, messages and profile only; their
  // role keeps what the phone app needs (utils/fieldStaffWebAccess.js).
  const isFieldStaff = isFieldStaffWebUser(user);
  const fullNavigation = getSidebarNavigation();
  const navigation = isFieldStaff ? trimNavigationForFieldStaff(fullNavigation) : fullNavigation;
  // Derived flag matching the gate inside getSidebarNavigation() — kept in sync
  // so the sidebar can render a skeleton while waiting for permissions/programs
  // /bundles. Without this, the navigation just returned [] and the sidebar
  // looked broken/empty on slow refreshes (see ProgramDashboard.outletRace).
  const isSidebarLoading =
    !isPlatformMode &&
    (sidebarLoading || LOADING_PROGRAMS);

  // Auto-expand current section (accordion style - only one open at a time)
  useEffect(() => {
    const currentPath = location.pathname;
    let sectionToExpand = null;

    // Check which section should be expanded based on current route
    // Admissions section (referrals, waitlist)
    if (currentPath.startsWith('/admin/referrals') ||
        currentPath.startsWith('/admin/intake-pipeline') ||
        currentPath.startsWith('/admin/da-scheduling') ||
        currentPath.startsWith('/admin/waitlist')) {
      sectionToExpand = 'admissions';
    // Programs section
    } else if (currentPath.startsWith('/admin/programs/')) {
      sectionToExpand = 'programs';
    } else if (currentPath === '/admin/programs') {
      sectionToExpand = 'admin'; // Program Management is now in Administration
    // Billing section (includes accounting)
    } else if (currentPath.startsWith('/admin/billing') ||
               currentPath.startsWith('/admin/accounting')) {
      sectionToExpand = 'billing';
    // People & HR section
    } else if (currentPath.startsWith('/admin/people-hr') || currentPath.startsWith('/admin/exclusion-checks')) {
      sectionToExpand = 'peopleHr';
    // Reports section
    } else if (currentPath.startsWith('/admin/reports')) {
      sectionToExpand = 'reports';
    // Communication section (includes contacts)
    } else if (currentPath.startsWith('/admin/communication') ||
               currentPath.startsWith('/admin/issues') ||
               currentPath.startsWith('/admin/contacts') ||
               currentPath.startsWith('/admin/announcements')) {
      sectionToExpand = 'communication';
    // Compliance section
    } else if (currentPath.startsWith('/admin/compliance')) {
      sectionToExpand = 'compliance';
    // Administration section
    } else if (
      currentPath.startsWith('/admin/settings') ||
      currentPath.startsWith('/admin/rbac') ||
      currentPath.startsWith('/admin/system') ||
      currentPath.startsWith('/admin/users') ||
      currentPath.startsWith('/admin/provider-types') ||
      currentPath.startsWith('/admin/permissions') ||
      currentPath.startsWith('/admin/offices') ||
      currentPath.startsWith('/admin/ai-analytics') ||
      currentPath.startsWith('/admin/knowledge-base') ||
      currentPath.startsWith('/admin/clinical-imports') ||
      currentPath.startsWith('/admin/data-import') ||
      currentPath.startsWith('/admin/feature-visibility')
    ) {
      sectionToExpand = 'admin';
    // Legacy routes
    } else if (currentPath.startsWith('/admin/caregivers')) {
      sectionToExpand = 'caregivers';
    } else if (currentPath.startsWith('/admin/visit-management') ||
               currentPath.startsWith('/admin/scheduler') ||
               currentPath.startsWith('/admin/visit-history') ||
               currentPath.startsWith('/admin/hha-status')) {
      sectionToExpand = 'visits';
    } else {
      // If we're on dashboard or other single pages, collapse all sections
      sectionToExpand = null;
    }

    // Auto-expand the section matching the current route, keep others as-is
    setExpandedSections(prev => {
      if (sectionToExpand && !prev[sectionToExpand]) {
        return { ...prev, [sectionToExpand]: true };
      }
      return prev;
    });
  }, [location.pathname]);

  // Bundles fetch — runs as soon as user is authenticated. Programs are now
  // owned by IntegratedSidebarProvider, so AdminShell only fetches bundles.
  useEffect(() => {
    // /bundles/my-bundles is org-scoped — platform admins have no tenant and always 401.
    if (!user || !user.organizationId) return;

    const fetchBundles = async () => {
      setBundlesLoadFailed(false);
      try {
        safeStorage.removeItem(ORG_BUNDLE_SLUGS_STORAGE_KEY);
        window.dispatchEvent(new Event(ORG_BUNDLE_SLUGS_UPDATED_EVENT));
      } catch (_) {}
      try {
        const response = await apiClient.get('/bundles/my-bundles');
        const bundles = response.data?.bundles || [];
        setOrgBundles(bundles);
        setBundlesLoaded(true);
        const slugs = bundles.map(ob => ob.bundle?.slug).filter(Boolean);
        try {
          safeStorage.setItem(ORG_BUNDLE_SLUGS_STORAGE_KEY, JSON.stringify(slugs));
          window.dispatchEvent(new Event(ORG_BUNDLE_SLUGS_UPDATED_EVENT));
        } catch (_) {}
      } catch (error) {
        console.error('Error fetching bundles:', error);
        setOrgBundles([]);
        setBundlesLoadFailed(true);
        setBundlesLoaded(true);
      }
    };

    fetchBundles();
  }, [user, apiClient]);

  // AuthContext's logout calls POST /users/logout (revokes and audits the server
  // session) before clearing storage, including evv_user_cache.
  const handleLogout = () => {
    logout();
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Click outside handler for profile dropdown.
  // The panel is rendered via createPortal at document.body, so it is NOT a
  // descendant of profileDropdownRef. We have to check both the trigger ref
  // and the portaled panel ref to know whether the click was "inside" the
  // logical dropdown.
  useEffect(() => {
    const handleClickOutside = (event) => {
      const insideTrigger = profileDropdownRef.current?.contains(event.target);
      const insidePanel = profilePanelRef.current?.contains(event.target);
      if (!insideTrigger && !insidePanel) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showProfileDropdown]);

  // On phones the sidebar is a drawer and always shows its labels
  const sidebarLabelsShown = sidebarOpen || isMobile;
  const sidebarBrandColor = isPlatformMode ? '#1C8A7D' : getColor('primary');
  const sidebarBrandInitial = organization?.name?.[0] || 'A';

  return (
    // Anchor the shell to the viewport: focus/scrollIntoView must only scroll
    // the content panels, never move the header/sidebar with the document.
    <div className="fixed inset-0 flex flex-col bg-arasync-gray dark:bg-wecare-dark-bg text-gray-800 dark:text-wecare-dark-text overflow-clip transition-colors duration-300">
      {/* Current on-call supervisor — shown once per session (BUG-A4B9B2DB) */}
      <OnCallPopup />
      {/* Full-Width Top Header - Always White (even in dark mode) */}
      <header
        inert={mobileDrawerOpen}
        className="border-b border-gray-200 dark:border-gray-300 px-4 py-3 flex items-center justify-between bg-white shadow-sm z-10"
        data-tour="header"
      >
        <div className="flex items-center gap-3">
          {isMobile && (
            <button
              ref={navigationTriggerRef}
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg text-gray-600 bg-white hover:bg-gray-100 border border-gray-200"
              aria-label="Open navigation menu"
              aria-expanded={sidebarOpen}
              aria-controls="admin-navigation-panel"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
          )}

          {/* AraSync Platform Logo - Always Visible */}
          <div
            className="flex items-center"
            role="img"
            aria-label="AraSync Health Systems"
          >
            <img
              src={araSyncMarkSvg}
              data-cobrowse-public-asset="arasync-mark"
              alt=""
              aria-hidden="true"
              className="h-11 w-10 sm:h-12 sm:w-11 shrink-0"
              onError={(e) => {
                console.error('AraSync mark failed to load:', e.target.src);
                e.target.style.display = 'none';
              }}
            />
            <span className="ml-2 flex flex-col justify-center text-[#1E2B3A] dark:text-white">
              <span className="text-[24px] sm:text-[25px] font-bold leading-[0.9] tracking-[-0.055em]">
                AraSync
              </span>
              <span className="mt-1.5 text-[7px] sm:text-[7.5px] font-semibold leading-none tracking-[0.24em] whitespace-nowrap">
                HEALTH SYSTEMS
              </span>
            </span>
          </div>
        </div>

        {/* Header Right Section - Alerts and User Profile */}
        <div className="flex items-center gap-4">
          {user?.organizationId && <LiveSupport key={`${user.organizationId}:${user.id}`} />}
          {can('view_notifications') && (
            <div data-tour="alerts">
              <AlertsDropdown />
            </div>
          )}
          <div data-tour="theme-toggle">
            <ThemeToggleButton />
          </div>

          {/* User Profile */}
          <div className="relative" ref={profileDropdownRef} data-tour="user-profile">
            <button
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              className="flex items-center gap-3 p-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div 
                className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium text-white border-2 bg-gray-600 border-gray-500"
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <span className="text-sm font-medium text-gray-700 hidden sm:inline">
                {user?.firstName} {user?.lastName}
              </span>
            </button>

          </div>

          {/* Profile Dropdown — portaled to body so it always renders above
              page-level toolbars (e.g. Time Tracking's z-[100]) regardless of
              the header's stacking context. Position is computed from the
              trigger button's bounding rect at render time. */}
          {showProfileDropdown && createPortal(
            (() => {
              const triggerRect = profileDropdownRef.current?.getBoundingClientRect();
              const top = triggerRect ? triggerRect.bottom + 8 : 80;
              // Right-align the 256px (w-64) panel under the trigger
              const right = triggerRect ? Math.max(8, window.innerWidth - triggerRect.right) : 16;
              return (
                <div
                  ref={profilePanelRef}
                  className="w-80 max-w-[calc(100vw-1rem)] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
                  style={{ position: 'fixed', top, right, zIndex: 9000 }}
                >
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-white font-medium bg-gray-600">
                        {user?.firstName?.[0]}{user?.lastName?.[0]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-gray-800 dark:text-white">
                          {user?.firstName} {user?.lastName}
                        </div>
                        <div
                          className="break-all text-sm leading-5 text-gray-600 dark:text-gray-400"
                          title={user?.email}
                        >
                          {user?.email}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="py-2">
                    <button
                      onClick={() => {
                        navigate('/admin/profile');
                        setShowProfileDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <span style={{ color: '#374151' }}>👤</span>
                      My Profile
                    </button>
                    <button
                      onClick={() => {
                        setShowChangePasswordModal(true);
                        setShowProfileDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <span style={{ color: '#374151' }}>🔐</span>
                      Change Password
                    </button>
                    <button
                      onClick={() => {
                        // A platform admin has no tenant, so /admin/issues is
                        // bounced back to the console by ProtectedRoute. Send
                        // them to the cross-tenant inbox instead.
                        navigate(user?.organizationId === null ? '/platform/support' : '/admin/issues');
                        setShowProfileDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <span style={{ color: '#374151' }}>🎫</span>
                      Support Tickets
                    </button>
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        handleLogout();
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      🚪 Logout
                    </button>
                  </div>
                </div>
              );
            })(),
            document.body
          )}
        </div>
      </header>

      {/* Main Layout Container */}
      <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="mobile-overlay"
          aria-hidden="true"
          onClick={closeMobileNavigation}
        />
      )}

      {/* Sidebar - Modern Design with Dark Mode Support.
          data-sidebar scopes the nav colour tokens in AdminShell.css and keeps
          the global button / gray-text overrides out of the navigation. */}
      <div ref={sidebarRef} id="admin-navigation-panel"
        role={mobileDrawerOpen ? 'dialog' : undefined}
        aria-label={mobileDrawerOpen ? 'Navigation menu' : undefined}
        aria-modal={mobileDrawerOpen || undefined}
        inert={isMobile && !sidebarOpen}
        tabIndex={-1}
        data-sidebar data-collapsed={sidebarLabelsShown ? undefined : 'true'} className={`
        ${isMobile ? 'sidebar-mobile' : 'sidebar-transition'}
        ${isMobile && sidebarOpen ? 'open' : ''}
        ${!isMobile && sidebarOpen ? 'sidebar-expanded' : 'sidebar-collapsed'}
        shrink-0 bg-white dark:bg-wecare-dark-surface border-r border-gray-200 flex flex-col transition-all duration-200 shadow-lg
      `}
      style={{
        borderRightColor: isActuallyDark ? 'rgba(255, 255, 255, 0.12)' : undefined
      }}
    >
        {/* Sidebar header: the organization's colour lives in the letter tile
            and a thin top rule, not a full coloured block */}
        <div
          data-sidebar-brand
          className="sidebar-brand"
          style={{ '--sidebar-brand-color': sidebarBrandColor }}
        >
          {sidebarLabelsShown ? (
            <div className="sidebar-brand-tile" aria-hidden="true">{sidebarBrandInitial}</div>
          ) : (
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="sidebar-brand-tile"
              title="Expand sidebar"
              aria-label="Expand sidebar"
            >
              {sidebarBrandInitial}
            </button>
          )}
          <div className="sidebar-fade flex-1 min-w-0" inert={!sidebarLabelsShown}>
            <div className="sidebar-brand-name dark:text-current">
              {organization?.name || 'AraSync'}
            </div>
            <div className="sidebar-brand-sub dark:text-current">
              {isPlatformMode ? 'Platform console' : 'EHR System'}
            </div>
          </div>
          {isMobile ? (
            <button type="button" onClick={closeMobileNavigation}
              className="sidebar-mobile-close-btn" aria-label="Close navigation menu">
              <XMarkIcon className="w-5 h-5" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="sidebar-fade sidebar-collapse-btn"
              inert={!sidebarLabelsShown}
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Navigation: flat rows. Sub-menus and labels stay mounted and are
            shown or hidden with CSS so they can animate (AdminShell.css). */}
        <nav data-cobrowse-navigation aria-label="Main navigation" className="flex-1 overflow-y-auto overflow-x-hidden py-2 px-2 flex flex-col">
          <div className="space-y-0.5 flex-1">
            {navigation.length === 0 && isSidebarLoading && (
              // Skeleton placeholders so a slow refresh doesn't look like a
              // broken/empty sidebar (Image #25 in BUG report 2026-05-16).
              <div aria-hidden="true" data-testid="sidebar-loading-skeleton" className="space-y-1.5 px-1">
                {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg">
                    <div className="h-4 w-4 rounded bg-gray-200 dark:bg-gray-700 animate-pulse flex-shrink-0" />
                    {sidebarLabelsShown && (
                      <div
                        className="h-3 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"
                        style={{ width: `${55 + ((i * 17) % 35)}%` }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
            {navigation.map((item) => (
              <div key={item.id}>
                {item.isHeading ? (
                  sidebarLabelsShown ? (
                    <p className="nav-heading px-2 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider">
                      {item.name}
                    </p>
                  ) : (
                    <div aria-hidden="true" className="mx-2 my-2 border-t border-gray-200 dark:border-gray-700" />
                  )
                ) : item.expandable ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        // From the icon rail, open the sidebar on that section
                        if (!sidebarLabelsShown) {
                          setSidebarOpen(true);
                          if (!item.expanded) toggleSection(item.id);
                          return;
                        }
                        toggleSection(item.id);
                      }}
                      aria-label={item.name}
                      aria-expanded={Boolean(item.expanded) && sidebarLabelsShown}
                      aria-controls={`nav-section-${item.id}`}
                      title={!sidebarLabelsShown ? item.name : undefined}
                      aria-current={item.current ? 'page' : undefined}
                      className={`nav-item w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left bg-transparent ${item.current ? 'is-current' : ''}`}
                    >
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      <span className="nav-label sidebar-fade dark:text-current" style={{ fontSize: '13px' }}>{item.name}</span>
                      <ChevronRightIcon
                        className={`nav-chevron sidebar-fade h-3.5 w-3.5 flex-shrink-0 ${item.expanded ? 'is-open' : ''}`}
                      />
                    </button>
                    <div
                      id={`nav-section-${item.id}`}
                      className={`nav-children ${item.expanded && sidebarLabelsShown ? 'is-open' : ''}`}
                      inert={!(item.expanded && sidebarLabelsShown)}
                    >
                      <div className="nav-children-inner">
                        <div className="nav-children-list">
                          {item.children.map((child, childIndex) => child.isHeading ? (
                            <div
                              key={child.href}
                              className="nav-heading px-2 pt-2.5 pb-0.5 uppercase tracking-wide font-semibold"
                              style={{ fontSize: '10px' }}
                            >
                              {child.name}
                            </div>
                          ) : (
                            <button
                              type="button"
                              key={child.href}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (child.badge === 'Coming Soon') {
                                  toast.error(`${child.name} functionality is coming soon!`);
                                } else {
                                  navigate(child.href);
                                }
                              }}
                              aria-current={(child.current ?? (location.pathname === child.href)) ? 'page' : undefined}
                              className={`nav-child w-full flex items-center gap-2 px-2 py-1 rounded-md text-left bg-transparent ${(child.current ?? (location.pathname === child.href)) ? 'is-current' : ''} ${child.badge === 'Coming Soon' ? 'is-coming-soon' : ''}`}
                              style={{ fontSize: '12px', '--nav-child-index': childIndex }}
                            >
                              {child.icon && <child.icon className="h-3.5 w-3.5 flex-shrink-0" />}
                              <span className="nav-label dark:text-current">{child.name}</span>
                              {child.badge && (
                                <span
                                  className={`px-1.5 py-0.5 rounded-full font-medium ${child.badge !== 'Active' ? 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300' : ''}`}
                                  style={child.badge === 'Active' ? {
                                    background: `#1C8A7D20`,
                                    color: '#1C8A7D',
                                    fontSize: '10px'
                                  } : {
                                    fontSize: '10px'
                                  }}
                                >
                                  {child.badge}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate(item.href)}
                    aria-label={item.name}
                    title={!sidebarLabelsShown ? item.name : undefined}
                    aria-current={item.current ? 'page' : undefined}
                    className={`nav-item w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left bg-transparent ${item.current ? 'is-current' : ''}`}
                  >
                    <item.icon className="h-4 w-4 flex-shrink-0" />
                    <span className="nav-label sidebar-fade dark:text-current" style={{ fontSize: '13px' }}>{item.name}</span>
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* AraSync Platform Branding Footer */}
          <div className="sidebar-footer mt-auto pt-2 pb-1 border-t" style={{ borderColor: isActuallyDark ? 'rgba(255, 255, 255, 0.12)' : '#E5E7EB' }}>
            <div className="flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity cursor-pointer">
              <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #1C8A7D, #4FD1C5)' }}>
                <span className="text-white font-bold" style={{ fontSize: '10px' }}>A</span>
              </div>
              <div className="sidebar-fade flex flex-col" inert={!sidebarLabelsShown}>
                <span className="nav-footer" style={{ fontSize: '9px', lineHeight: '1.2' }}>
                  Powered by
                </span>
                <span className="font-semibold" style={{ fontSize: '11px', lineHeight: '1.2', color: '#1C8A7D' }}>
                  AraSync
                </span>
              </div>
            </div>
          </div>
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0 flex flex-col h-full overflow-hidden">
        {/* Main Content */}
        <main
          inert={mobileDrawerOpen}
          ref={mainContentRef}
          id="main-content"
          className="flex-1 min-w-0 bg-arasync-gray dark:bg-wecare-dark-surface overflow-y-auto overflow-x-hidden transition-colors duration-200"
        >
          <Breadcrumbs />
          <ErrorBoundary>
            <PageTransition>
              {/* Room below the last row for the floating Policy Assistant
                  button (fixed bottom-6 right-6, 56px), so it never covers a
                  page's last control (a pager arrow, a Send Test, an Approve). */}
              <div className={`w-full min-w-0 ${hasOrgFeature('knowledge_base') ? 'pb-24' : ''}`}>
                {isFieldStaff && !fieldStaffMayOpen(location.pathname) ? (
                  // Another page's URL (a 245D program, say) goes home to
                  // /admin, so the breadcrumb does not name a page they lack.
                  fieldStaffHomeRedirect(location.pathname)
                    ? <Navigate to={fieldStaffHomeRedirect(location.pathname)} replace />
                    : <FieldStaffWebHome />
                ) : (
                  <Outlet context={{
                    isCoResponderOnly,
                    coResponderProgramId,
                    bundlesLoaded,
                    bundlesLoadFailed,
                    orgBundles,
                    programs,
                  }} />
                )}
              </div>
            </PageTransition>
          </ErrorBoundary>
        </main>
      </div>
      </div> {/* Close Main Layout Container */}

      <ChangePasswordModal
        open={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        token={safeStorage.getItem("evv_token")}
      />

      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        navigate={navigate}
        hiddenCommandIds={bundlesLoaded && !bundlesLoadFailed && !orgHasBundle('home-health') ? ['visits'] : []}
      />
      <KeyboardShortcutsHelp isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
      <NotificationCenter isOpen={showNotifications} onClose={() => setShowNotifications(false)} />

      {/* Floating Policy Assistant Button - owner organization only */}
      {hasOrgFeature('knowledge_base') && (
        <button
          ref={fabRef}
          onPointerDown={handleFabPointerDown}
          onPointerMove={handleFabPointerMove}
          onPointerUp={handleFabPointerUp}
          style={fabDisplayPosition ? { position: 'fixed', left: fabDisplayPosition.left, top: fabDisplayPosition.top, bottom: 'auto', right: 'auto' } : undefined}
          className={`${fabDisplayPosition ? 'fixed' : 'fixed bottom-6 right-6'} w-14 h-14 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-full shadow-lg hover:shadow-xl transition-shadow duration-200 flex items-center justify-center z-40 group cursor-grab active:cursor-grabbing touch-none select-none`}
          title="Policy Assistant - Drag to move, click to open"
        >
          <BookOpenIcon className="h-6 w-6 pointer-events-none" />
          <span className="absolute right-full mr-3 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Policy Assistant
          </span>
        </button>
      )}
    </div>
  );
}
